import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import MessagesList from "./MessagesList";
import MessageInput from "./MessageInput";
import ChatHeader from "./ChatHeader";
import TypingIndicator from "./TypingIndicator";
import MuteDurationModal from "./MuteDurationModal";
import DeleteConversationModal from "./DeleteConversationModal";
import ClearConversationModal from "./ClearConversationModal";
import ThemeManager from "./ThemeManager";
import MessageInfoModal from "./MessageInfoModal";
import GroupInfoModal from "./GroupInfoModal";
import LeaveGroupModal from "./LeaveGroupModal";
import DeleteGroupModal from "./DeleteGroupModal";
import ForwardMessageModal from "./ForwardMessageModal";
import { messengerService } from "../../../functions/messenger";
import { getFriendsPageInfos } from "../../../functions/user";
import { getCustomLists, updateCustomList } from "../../../functions/customLists";
import { uploadImages } from "../../../functions/uploadImages";
import "./style.css";

// WhatsApp-style window for "Delete for everyone" — after this many minutes
// the option is no longer offered, matching the ~2-day grace period users
// expect from modern messengers.
const DELETE_FOR_EVERYONE_WINDOW_MINUTES = 2 * 24 * 60; // 48 hours

export default function ChatWindow({
  conversation,
  messages,
  currentUser,
  onSendMessage,
  onSendMedia,
  onStartCall,
  typingUsers,
  socket,
  messagesEndRef,
  onBack,
  onClearMessages,
  onShowCallHistory,
  onlineUsers,
  blockedUserIds = [],
  onConversationPinToggle,
  onMessageDeleted,
  onEditMessage,
  conversations = [],
  onForwardMessage,
  onOpenProfile,
  jumpToMessageId,
  onJumpHandled,
  onLeaveGroup,
  onDeleteGroup,
}) {
  const navigate = useNavigate();
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showMuteDurationModal, setShowMuteDurationModal] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showThemeManager, setShowThemeManager] = useState(false);
  const [showGroupInfoModal, setShowGroupInfoModal] = useState(false);
  const [showLeaveGroupModal, setShowLeaveGroupModal] = useState(false);
  const [isLeavingGroup, setIsLeavingGroup] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);
  const [followingIds, setFollowingIds] = useState(() => Array.isArray(currentUser?.following) ? currentUser.following : []);
  const [currentGroupList, setCurrentGroupList] = useState(null);
  const [isUploadingGroupPhoto, setIsUploadingGroupPhoto] = useState(false);
  const [groupPictureOverride, setGroupPictureOverride] = useState("");
  const [currentTheme, setCurrentTheme] = useState(null);
  // Selection mode state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());
  const [starRefreshKey, setStarRefreshKey] = useState(0);
  const [messageInfoMessage, setMessageInfoMessage] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardTargetMessage, setForwardTargetMessage] = useState(null);
  const [isForwarding, setIsForwarding] = useState(false);
  const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // Whether the current user has blocked the other participant of a private
  // conversation. The block stays silent from the BLOCKED user's side (they
  // never know they're blocked — handled server-side via `notDeliveredTo`).
  // From the BLOCKER's side, however, the open chat shows an explicit
  // "You blocked this user" notice in place of the message input with an
  // Unblock CTA, similar to WhatsApp.
  const iBlockedOtherUser = useMemo(() => {
    if (conversation.type !== 'private') return false;
    const otherUser = conversation.participants?.find(p => p._id !== currentUser.id);
    if (!otherUser) return false;
    const otherId = String(otherUser._id || otherUser.id || '');
    return (blockedUserIds || []).some((id) => String(id) === otherId);
  }, [conversation, currentUser.id, blockedUserIds]);

  // True when the current user has left this group conversation. They keep
  // read-only access to the chat history but cannot send or receive new
  // messages.
  const hasUserLeftGroup = useMemo(() => {
    if (!conversation || conversation.type !== "group") return false;
    if (conversation.userLeft) return true;
    const userIdStr = String(currentUser?.id || currentUser?._id || "");
    if (Array.isArray(conversation.leftMembers)) {
      return conversation.leftMembers.some(
        (entry) => String(entry?.user?._id || entry?.user || "") === userIdStr
      );
    }
    if (Array.isArray(conversation.participants) && userIdStr) {
      const stillParticipant = conversation.participants.some(
        (p) => String(p?._id || p?.id || p) === userIdStr
      );
      // Fallback: if the conversation explicitly excludes this user from
      // participants but is still being shown (because the server flagged it
      // as readable for them), treat it as left.
      if (!stillParticipant && conversation.userLeft === undefined) return false;
      return !stillParticipant;
    }
    return false;
  }, [conversation, currentUser?.id, currentUser?._id]);

  // The chat is read-only (no sending, replying, reacting, editing) when the
  // current user has left a group OR has blocked the other participant of a
  // private chat. Both cases swap the MessageInput for a banner.
  const isReadOnlyConversation = hasUserLeftGroup || iBlockedOtherUser;

  // Compute if all selected messages are starred (for toggling star/unstar UI)
  const allSelectedAreStarred = (() => {
    try {
      if (!isSelecting || !selectedMessageIds || selectedMessageIds.size === 0) return false;
      const store = JSON.parse(localStorage.getItem('starredMessages') || '{}');
      for (const id of selectedMessageIds) {
        const msg = (messages || []).find((m) => m._id === id);
        const isStar = (store && store[id]) || (msg && (msg.starred || msg.isStarred));
        if (!isStar) return false;
      }
      return true;
    } catch {
      return false;
    }
  })();
  const selectedSingleMessage = useMemo(() => {
    try {
      if (!isSelecting || !selectedMessageIds || selectedMessageIds.size !== 1) return null;
      const onlyId = Array.from(selectedMessageIds)[0];
      return (messages || []).find((m) => m._id === onlyId) || null;
    } catch {
      return null;
    }
  }, [isSelecting, selectedMessageIds, messages]);
  const canShowSelectedMessageInfo = !!(selectedSingleMessage && selectedSingleMessage.sender && selectedSingleMessage.sender._id === currentUser.id);

  // True when every selected message is owned by the current user AND every
  // one of them is still within the WhatsApp-style "Delete for everyone"
  // grace window (DELETE_FOR_EVERYONE_WINDOW_MINUTES). If even one selected
  // message belongs to someone else, or any own message is past the window,
  // we fall back to "Delete for me" only — matching WhatsApp's UX.
  const canDeleteSelectedForEveryone = useMemo(() => {
    try {
      if (!isSelecting || !selectedMessageIds || selectedMessageIds.size === 0) return false;
      const windowMs = DELETE_FOR_EVERYONE_WINDOW_MINUTES * 60 * 1000;
      const now = Date.now();
      for (const id of selectedMessageIds) {
        const msg = (messages || []).find((m) => m._id === id);
        if (!msg || !msg.sender || msg.sender._id !== currentUser.id) return false;
        const createdAtMs = msg.createdAt ? new Date(msg.createdAt).getTime() : 0;
        if (!createdAtMs || now - createdAtMs > windowMs) return false;
      }
      return true;
    } catch {
      return false;
    }
  }, [isSelecting, selectedMessageIds, messages, currentUser.id]);

  const handleTypingStart = () => {
    if (!isTyping && socket) {
      setIsTyping(true);
      socket.emit('typing-start', {
        conversationId: conversation._id,
        participants: conversation.participants.map(p => p._id),
      });
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing after 2 seconds
    typingTimeoutRef.current = setTimeout(() => {
      handleTypingStop();
    }, 2000);
  };

  const handleTypingStop = () => {
    if (isTyping && socket) {
      setIsTyping(false);
      socket.emit('typing-stop', {
        conversationId: conversation._id,
        participants: conversation.participants.map(p => p._id),
      });
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  const handleSendMessage = async (messageData) => {
    handleTypingStop();
    
    const finalMessageData = {
      ...messageData,
      replyTo: replyingTo?._id,
    };

    await onSendMessage(finalMessageData);
    setReplyingTo(null);
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      await onSendMedia(files);
    }
    // Clear the input
    event.target.value = '';
  };

  // Load saved theme when conversation changes
  useEffect(() => {
    if (conversation?._id) {
      try {
        const savedThemes = JSON.parse(localStorage.getItem('messengerThemes') || '{}');
        const savedTheme = savedThemes[conversation._id];
        if (savedTheme) {
          setCurrentTheme(savedTheme);
          applyTheme(savedTheme);
        } else {
          setCurrentTheme(null);
          applyTheme(null);
        }
      } catch (error) {
        console.error('Error loading theme:', error);
        setCurrentTheme(null);
        applyTheme(null);
      }
    }
  }, [conversation?._id]);

  const applyTheme = (theme) => {
    try {
      const chatWindow = document.querySelector('.chat_window');
      if (!chatWindow) return;

      if (theme && (theme.type === 'custom' || (theme.background && theme.background !== 'var(--bg-primary)' && theme.background !== '#ffffff'))) {
        chatWindow.classList.add('themed');
        
        if (theme.type === 'custom' && theme.customImage) {
          chatWindow.style.background = `linear-gradient(rgba(0,0,0,${theme.opacity || 0.3}), rgba(0,0,0,${theme.opacity || 0.3})), url(${theme.customImage})`;
          chatWindow.style.backgroundSize = 'cover';
          chatWindow.style.backgroundPosition = 'center';
          chatWindow.style.backgroundAttachment = 'fixed';
          chatWindow.style.filter = theme.blur ? `blur(${theme.blur}px)` : 'none';
        } else if (theme.background) {
          chatWindow.style.background = theme.background;
          chatWindow.style.backgroundSize = 'cover';
          chatWindow.style.backgroundPosition = 'center';
          chatWindow.style.backgroundAttachment = 'fixed';
          chatWindow.style.filter = 'none';
        }
      } else {
        chatWindow.classList.remove('themed');
        chatWindow.style.background = 'var(--bg-primary)';
        chatWindow.style.backgroundSize = 'auto';
        chatWindow.style.backgroundPosition = 'auto';
        chatWindow.style.backgroundAttachment = 'auto';
        chatWindow.style.filter = 'none';
      }
    } catch (error) {
      console.error('Error applying theme:', error);
      // Fallback to default theme
      const chatWindow = document.querySelector('.chat_window');
      if (chatWindow) {
        chatWindow.classList.remove('themed');
        chatWindow.style.background = 'var(--bg-primary)';
        chatWindow.style.backgroundSize = 'auto';
        chatWindow.style.backgroundPosition = 'auto';
        chatWindow.style.backgroundAttachment = 'auto';
        chatWindow.style.filter = 'none';
      }
    }
  };

  const handleThemeChange = (newTheme) => {
    setCurrentTheme(newTheme);
    applyTheme(newTheme);
  };

  const handleReactToMessage = async (messageId, emoji) => {
    if (isReadOnlyConversation) return;
    try {
      await messengerService.reactToMessage(messageId, emoji, currentUser.token);
    } catch (error) {
      console.error("Error reacting to message:", error);
    }
  };

  // Unblock the other participant straight from the in-chat banner.
  const handleUnblockFromBanner = async () => {
    if (conversation?.type !== 'private') return;
    const otherUser = conversation.participants?.find(
      (p) => p._id !== currentUser.id
    );
    if (!otherUser?._id) return;
    try {
      await messengerService.unblockUser(otherUser._id, currentUser.token);
      window.dispatchEvent(new CustomEvent('blockedUsersUpdated'));
    } catch (error) {
      console.error('Error unblocking user from banner:', error);
    }
  };

  const handleDeleteMessage = async (messageId, deleteForEveryone = false) => {
    // Optimistically update the UI immediately (synchronous, before API call)
    if (onMessageDeleted) {
      onMessageDeleted(messageId, deleteForEveryone);
    }
    
    try {
      // Then call the API
      await messengerService.deleteMessage(messageId, deleteForEveryone, currentUser.token);
    } catch (error) {
      console.error("Error deleting message:", error);
      // On error, we could revert the optimistic update, but for now we'll let the socket event handle it
    }
  };

  const handleEditMessage = async (messageId, text) => {
    if (isReadOnlyConversation) return;
    if (onEditMessage) {
      await onEditMessage(messageId, text);
    }
  };

  const handleReplyToMessage = (message) => {
    if (isReadOnlyConversation) return;
    setReplyingTo(message);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  // Selection handlers
  const startSelectingMessages = () => {
    setIsSelecting(true);
    setSelectedMessageIds(new Set());
  };

  const cancelSelectingMessages = () => {
    setIsSelecting(false);
    setSelectedMessageIds(new Set());
  };

  const toggleSelectMessage = (messageId) => {
    setSelectedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  };

  const buildStarredEntry = (message) => {
    if (!message || !message._id) return null;
    const preview =
      message.type === 'text' ? (message.content?.text || '') :
      message.type === 'image' ? '[Image]' :
      message.type === 'video' ? '[Video]' :
      message.type === 'audio' ? '[Audio]' :
      message.type === 'file' ? '[File]' :
      message.type === 'contact' ? `[Contact] ${message.content?.contact?.name || ''}` :
      message.type === 'location' ? '[Location]' :
      message.type === 'call' ? '[Call]' : '[Message]';

    return {
      messageId: message._id,
      conversationId: conversation?._id,
      senderId: message.sender?._id || '',
      senderName: `${message.sender?.first_name || ''} ${message.sender?.last_name || ''}`.trim() || 'Unknown User',
      senderPicture: message.sender?.picture || '',
      preview,
      createdAt: message.createdAt,
      starredAt: new Date().toISOString(),
    };
  };

  const starSelectedMessages = () => {
    if (selectedMessageIds.size === 0) return;
    try {
      const store = JSON.parse(localStorage.getItem('starredMessages') || '{}');
      const dataStore = JSON.parse(localStorage.getItem('starredMessagesData') || '{}');
      selectedMessageIds.forEach((id) => { store[id] = true; });
      selectedMessageIds.forEach((id) => {
        const msg = (messages || []).find((m) => m._id === id);
        const entry = buildStarredEntry(msg);
        if (entry) dataStore[id] = entry;
      });
      localStorage.setItem('starredMessages', JSON.stringify(store));
      localStorage.setItem('starredMessagesData', JSON.stringify(dataStore));
      // Trigger child bubbles to refresh their local starred state
      setStarRefreshKey((k) => k + 1);
      window.dispatchEvent(new CustomEvent('starredMessagesUpdated'));
      // Small toast
      const n = document.createElement('div');
      n.textContent = `${selectedMessageIds.size} message(s) starred`;
      n.style.cssText = `position: fixed; top: 20px; right: 20px; background: #ffd43b; color: #000; padding: 12px 20px; border-radius: 8px; z-index: 10000; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.2);`;
      document.body.appendChild(n);
      setTimeout(() => n.remove(), 2000);
    } catch {}
  };

  const unstarSelectedMessages = () => {
    if (selectedMessageIds.size === 0) return;
    try {
      const store = JSON.parse(localStorage.getItem('starredMessages') || '{}');
      const dataStore = JSON.parse(localStorage.getItem('starredMessagesData') || '{}');
      selectedMessageIds.forEach((id) => { delete store[id]; });
      selectedMessageIds.forEach((id) => { delete dataStore[id]; });
      localStorage.setItem('starredMessages', JSON.stringify(store));
      localStorage.setItem('starredMessagesData', JSON.stringify(dataStore));
      // Trigger child bubbles to refresh their local starred state
      setStarRefreshKey((k) => k + 1);
      window.dispatchEvent(new CustomEvent('starredMessagesUpdated'));
      // Small toast
      const n = document.createElement('div');
      n.textContent = `${selectedMessageIds.size} message unstarred`;
      n.style.cssText = `position: fixed; top: 20px; right: 20px; background: #adb5bd; color: #000; padding: 12px 20px; border-radius: 8px; z-index: 10000; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.2);`;
      document.body.appendChild(n);
      setTimeout(() => n.remove(), 2000);
    } catch {}
  };

  // Open the delete-selected confirmation modal. The actual deletion only
  // happens once the user confirms via `confirmDeleteSelectedMessages`.
  const requestDeleteSelectedMessages = () => {
    if (!selectedMessageIds || selectedMessageIds.size === 0) return;
    setShowDeleteSelectedModal(true);
  };

  const confirmDeleteSelectedMessages = async (deleteForEveryone = false) => {
    if (!selectedMessageIds || selectedMessageIds.size === 0) return;
    // Guard against "for everyone" when the set contains messages the user
    // doesn't own — the header gates this in UI, but enforce it here too.
    if (deleteForEveryone && !canDeleteSelectedForEveryone) {
      deleteForEveryone = false;
    }
    try {
      setIsDeletingSelected(true);
      const ids = Array.from(selectedMessageIds);
      await Promise.all(
        ids.map((messageId) => handleDeleteMessage(messageId, deleteForEveryone))
      );
      showTransientNotification(
        `${ids.length} message${ids.length === 1 ? '' : 's'} deleted`,
        '#51cf66'
      );
      setShowDeleteSelectedModal(false);
      cancelSelectingMessages();
    } catch (error) {
      console.error('Error deleting selected messages:', error);
      showTransientNotification('Failed to delete messages. Please try again.', '#ff6b6b');
    } finally {
      setIsDeletingSelected(false);
    }
  };

  const copySelectedMessages = async () => {
    if (selectedMessageIds.size === 0) return;
    try {
      const setIds = new Set(selectedMessageIds);
      const texts = (messages || [])
        .filter((m) => setIds.has(m._id))
        .map((m) => {
          if (m.type === 'text' && m.content?.text) return m.content.text;
          if (m.type === 'image') return '[Image]';
          if (m.type === 'video') return '[Video]';
          if (m.type === 'audio') return '[Audio]';
          if (m.type === 'file') return '[File]';
          if (m.type === 'contact') return `[Contact] ${m.content?.contact?.name || ''}`;
          if (m.type === 'location') return '[Location]';
          if (m.type === 'call') return '[Call]';
          return '[Message]';
        });
      const text = texts.join('\n');
      await navigator.clipboard.writeText(text);
      const n = document.createElement('div');
      n.textContent = `Copied ${selectedMessageIds.size} message(s)`;
      n.style.cssText = `position: fixed; top: 20px; right: 20px; background: #51cf66; color: #fff; padding: 12px 20px; border-radius: 8px; z-index: 10000; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.2);`;
      document.body.appendChild(n);
      setTimeout(() => n.remove(), 2000);
    } catch (e) {
      alert('Unable to copy messages');
    }
  };

  const handleMute = (muteData) => {
    // Update conversation mute status with duration
    conversation.muted = true;
    conversation.mutedUntil = muteData.mutedUntil;
    conversation.muteDuration = muteData.duration;
    
    // Show feedback to user
    const durationText = muteData.duration === 'always' ? 'Always' : 
      muteData.duration === '1_day' ? '1 day' :
      muteData.duration === '1_week' ? '1 week' :
      muteData.duration === '1_month' ? '1 month' : '1 day';
    
    const notification = document.createElement('div');
    notification.textContent = `Conversation muted for ${durationText}`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ff6b6b;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      z-index: 10000;
      font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
    
    // You would typically make an API call here to update the conversation mute status
    // await messengerService.muteConversation(conversation._id, muteData, currentUser.token);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    
    // Search through messages
    const results = messages.filter(message => {
      if (message.type === 'text' && message.content?.text) {
        return message.content.text.toLowerCase().includes(query.toLowerCase());
      }
      return false;
    });
    
    setSearchResults(results);
    setCurrentSearchIndex(0);
    
    // Scroll to first result if found
    if (results.length > 0) {
      scrollToMessage(results[0]._id);
    }
  };

  const scrollToMessage = (messageId) => {
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
    if (messageElement) {
      messageElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Highlight the message briefly
      messageElement.style.backgroundColor = 'rgba(24, 118, 242, 0.1)';
      setTimeout(() => {
        messageElement.style.backgroundColor = '';
      }, 2000);
    }
  };

  useEffect(() => {
    if (!jumpToMessageId || !Array.isArray(messages) || messages.length === 0) return;
    const exists = messages.some((message) => message && message._id === jumpToMessageId);
    if (!exists) return;

    const timer = setTimeout(() => {
      const messageElement = document.querySelector(`[data-message-id="${jumpToMessageId}"]`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        messageElement.classList.add('jump_highlight');
        setTimeout(() => messageElement.classList.remove('jump_highlight'), 2200);
      }
      if (onJumpHandled) onJumpHandled();
    }, 120);

    return () => clearTimeout(timer);
  }, [jumpToMessageId, messages, onJumpHandled]);

  const goToNextResult = () => {
    if (searchResults.length === 0) return;
    
    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);
    scrollToMessage(searchResults[nextIndex]._id);
  };

  const goToPreviousResult = () => {
    if (searchResults.length === 0) return;
    
    const prevIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(prevIndex);
    scrollToMessage(searchResults[prevIndex]._id);
  };

  const closeSearch = () => {
    setIsSearching(false);
    setSearchQuery('');
    setSearchResults([]);
    setCurrentSearchIndex(0);
  };

  const normalizeId = useCallback((value) => {
    if (!value) return '';
    if (typeof value === 'object') {
      if (value._id) return normalizeId(value._id);
      if (value.id) return normalizeId(value.id);
    }
    return String(value);
  }, []);

  useEffect(() => {
    setGroupPictureOverride(conversation?.groupPicture || conversation?.picture || "");
  }, [conversation?._id, conversation?.groupPicture, conversation?.picture]);

  const resolveGroupListForConversation = useCallback((customLists = []) => {
    if (!conversation || conversation.type !== "group" || !Array.isArray(customLists) || customLists.length === 0) {
      return null;
    }

    const conversationName = (conversation.name || "").trim().toLowerCase();
    const participantIds = new Set((conversation.participants || []).map((participant) => normalizeId(participant?._id || participant?.id)).filter(Boolean));

    const scored = customLists.map((list) => {
      const listName = (list?.name || "").trim().toLowerCase();
      const listIds = [
        normalizeId(list?.owner?._id || list?.owner?.id || list?.owner),
        ...(Array.isArray(list?.admins) ? list.admins.map((admin) => normalizeId(admin?._id || admin?.id || admin)) : []),
        ...(Array.isArray(list?.members) ? list.members.map((member) => normalizeId(member?._id || member?.id || member)) : []),
      ].filter(Boolean);

      const uniqueListIds = new Set(listIds);
      let overlap = 0;
      participantIds.forEach((participantId) => {
        if (uniqueListIds.has(participantId)) overlap += 1;
      });

      return {
        list,
        nameMatch: listName && listName === conversationName ? 1 : 0,
        overlap,
      };
    });

    scored.sort((a, b) => {
      if (a.nameMatch !== b.nameMatch) return b.nameMatch - a.nameMatch;
      return b.overlap - a.overlap;
    });

    const best = scored[0];
    return best && (best.nameMatch > 0 || best.overlap > 0) ? best.list : null;
  }, [conversation, normalizeId]);

  const currentUserId = normalizeId(currentUser?.id || currentUser?._id);
  const conversationAdminId = normalizeId(conversation?.admin);
  const isCurrentUserGroupAdmin =
    conversation?.type === "group" &&
    !hasUserLeftGroup &&
    (
      conversationAdminId === currentUserId ||
      currentGroupList?.userRole === "owner" ||
      currentGroupList?.userRole === "admin"
    );
  const canEditGroupPhoto = isCurrentUserGroupAdmin;

  // "Owner" is the primary admin — the one who must transfer admin access
  // before leaving the group. The CustomList is the source of truth, with
  // a fallback to the conversation's admin field for legacy groups.
  const groupOwnerId = normalizeId(
    currentGroupList?.owner?._id ||
      currentGroupList?.owner?.id ||
      currentGroupList?.owner ||
      conversation?.admin
  );
  const isCurrentUserGroupOwner =
    conversation?.type === "group" &&
    !hasUserLeftGroup &&
    !!groupOwnerId &&
    groupOwnerId === currentUserId;

  // Keep `currentGroupList` in sync with live conversation participants.
  // When someone leaves, drop them from the member/admin lists but preserve
  // Admin roles for everyone who remains an admin.
  useEffect(() => {
    if (!currentGroupList || conversation?.type !== "group") return;
    if (!Array.isArray(conversation?.participants)) return;
    const participantIds = new Set(
      conversation.participants
        .map((p) => normalizeId(p?._id || p?.id || p))
        .filter(Boolean)
    );
    if (participantIds.size === 0) return;
    setCurrentGroupList((prev) => {
      if (!prev || !Array.isArray(prev.members)) return prev;

      const filteredMembers = prev.members.filter((m) => {
        const mid = normalizeId(m?._id || m?.id);
        return mid && participantIds.has(mid);
      });
      const filteredAdmins = Array.isArray(prev.admins)
        ? prev.admins.filter((admin) => {
            const adminId = normalizeId(admin?._id || admin?.id || admin);
            return adminId && participantIds.has(adminId);
          })
        : prev.admins;

      const adminIds = new Set(
        (Array.isArray(filteredAdmins) ? filteredAdmins : [])
          .map((admin) => normalizeId(admin?._id || admin?.id || admin))
          .filter(Boolean)
      );
      const ownerId = normalizeId(prev.owner?._id || prev.owner?.id || prev.owner);
      if (ownerId && participantIds.has(ownerId)) {
        adminIds.add(ownerId);
      }

      const membersWithRoles = filteredMembers.map((member) => {
        const memberId = normalizeId(member?._id || member?.id);
        if (memberId && adminIds.has(memberId)) {
          return { ...member, role: "admin" };
        }
        return member;
      });

      const ownerStillPresent = ownerId && participantIds.has(ownerId);
      const nextOwner = ownerStillPresent
        ? prev.owner
        : membersWithRoles.find((m) => normalizeId(m?._id || m?.id) === normalizeId(conversation?.admin)) ||
          (Array.isArray(filteredAdmins) && filteredAdmins.length > 0 ? filteredAdmins[0] : prev.owner);

      if (
        filteredMembers.length === prev.members.length &&
        (!Array.isArray(prev.admins) || filteredAdmins.length === prev.admins.length) &&
        normalizeId(nextOwner?._id || nextOwner?.id || nextOwner) === ownerId
      ) {
        return prev;
      }

      return {
        ...prev,
        owner: nextOwner,
        admins: filteredAdmins,
        members: membersWithRoles,
      };
    });
  }, [conversation?.participants, conversation?.admin, conversation?.type, normalizeId, currentGroupList]);

  // When the conversation admin field changes after an explicit handoff, sync
  // the list owner without altering other admins' roles.
  useEffect(() => {
    if (!currentGroupList || conversation?.type !== "group") return;
    const newAdminId = normalizeId(conversation?.admin);
    if (!newAdminId) return;
    setCurrentGroupList((prev) => {
      if (!prev) return prev;
      const currentOwnerId = normalizeId(
        prev.owner?._id || prev.owner?.id || prev.owner
      );
      if (currentOwnerId === newAdminId) return prev;

      const adminIds = new Set(
        (Array.isArray(prev.admins) ? prev.admins : [])
          .map((admin) => normalizeId(admin?._id || admin?.id || admin))
          .filter(Boolean)
      );
      adminIds.add(newAdminId);

      const newOwnerMember = Array.isArray(prev.members)
        ? prev.members.find(
            (m) => normalizeId(m?._id || m?.id) === newAdminId
          )
        : null;
      const updatedMembers = Array.isArray(prev.members)
        ? prev.members.map((m) => {
            const mid = normalizeId(m?._id || m?.id);
            if (mid && adminIds.has(mid)) return { ...m, role: "admin" };
            return m;
          })
        : prev.members;

      let nextUserRole = prev.userRole;
      if (currentUserId === newAdminId) {
        nextUserRole = "owner";
      } else if (adminIds.has(currentUserId)) {
        nextUserRole = "admin";
      }

      return {
        ...prev,
        owner: newOwnerMember || prev.owner,
        members: updatedMembers,
        userRole: nextUserRole,
      };
    });
  }, [conversation?.admin, conversation?.type, currentUserId, normalizeId, currentGroupList]);

  const handleGroupPhotoChange = async (file) => {
    if (!file || !canEditGroupPhoto || !currentUser?.token) return;

    try {
      setIsUploadingGroupPhoto(true);

      const path = `${currentUser.username || "user"}/group_pictures`;
      const formData = new FormData();
      formData.append("path", path);
      formData.append("file", file);

      const uploadResponse = await uploadImages(formData, path, currentUser.token);
      const uploadedUrl = Array.isArray(uploadResponse) && uploadResponse.length > 0 ? uploadResponse[0]?.url : null;

      if (!uploadedUrl) {
        throw new Error("Failed to upload group photo");
      }

      if (currentGroupList?.id) {
        const updateResponse = await updateCustomList(
          currentGroupList.id,
          {
            name: currentGroupList.name || conversation?.name || "Group Chat",
            description: currentGroupList.description || "",
            groupPicture: uploadedUrl,
          },
          currentUser.token
        );

        if (updateResponse?.status !== "ok") {
          throw new Error(updateResponse?.message || "Failed to update group photo");
        }

        setCurrentGroupList((prev) => prev ? { ...prev, groupPicture: uploadedUrl } : prev);
      }

      setGroupPictureOverride(uploadedUrl);
    } catch (error) {
      console.error("Failed to update group photo:", error);
      alert("Failed to update group photo. Please try again.");
    } finally {
      setIsUploadingGroupPhoto(false);
    }
  };

  const navigateToProfileByUserId = (userId) => {
    if (!userId) return;
    const targetId = normalizeId(userId);
    const otherUser = conversation.participants.find((p) => normalizeId(p?._id || p?.id) === targetId);
    if (otherUser && otherUser.username) {
      if (onOpenProfile) {
        onOpenProfile({ userId: targetId, username: otherUser.username });
      } else {
        navigate(`/profile/${otherUser.username}`);
      }
      setShowGroupInfoModal(false);
      return;
    }
    console.error('User not found or username not available');
  };

  const handleForwardMessage = (message) => {
    if (!message || isReadOnlyConversation) return;
    setForwardTargetMessage(message);
    setShowForwardModal(true);
  };

  const handleConfirmForward = async (conversationIds) => {
    if (!forwardTargetMessage || !Array.isArray(conversationIds) || conversationIds.length === 0) return;
    try {
      setIsForwarding(true);
      if (onForwardMessage) {
        await onForwardMessage(forwardTargetMessage, conversationIds);
      } else {
        await messengerService.forwardMessage(forwardTargetMessage._id, conversationIds, currentUser.token);
      }
      setShowForwardModal(false);
      setForwardTargetMessage(null);
    } catch (error) {
      console.error("Error forwarding message:", error);
      alert("Unable to forward message. Please try again.");
    } finally {
      setIsForwarding(false);
    }
  };

  const showTransientNotification = (text, background = "#51cf66") => {
    const notification = document.createElement("div");
    notification.textContent = text;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${background};
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      z-index: 10000;
      font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (notification.parentNode) notification.parentNode.removeChild(notification);
    }, 3000);
  };

  const handleRemoveGroupMember = async (memberId, member) => {
    if (!conversation || conversation.type !== "group" || !currentUser?.token) return;
    if (!memberId) return;
    if (!isCurrentUserGroupAdmin) {
      showTransientNotification("Only group admins can remove members.", "#ff6b6b");
      return;
    }

    try {
      await messengerService.removeGroupMember(
        conversation._id,
        memberId,
        currentUser.token
      );

      // Optimistically update local group + conversation state so the modal
      // reflects the removal immediately. The socket event will reconcile
      // the rest of the UI for everyone else.
      setCurrentGroupList((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          members: (prev.members || []).filter(
            (m) => normalizeId(m?._id || m?.id || m) !== memberId
          ),
          admins: Array.isArray(prev.admins)
            ? prev.admins.filter(
                (m) => normalizeId(m?._id || m?.id || m) !== memberId
              )
            : prev.admins,
        };
      });

      const displayName =
        [member?.first_name, member?.last_name].filter(Boolean).join(" ").trim() ||
        member?.name ||
        member?.username ||
        "Member";
      showTransientNotification(`${displayName} removed from group`);
    } catch (error) {
      console.error("Error removing group member:", error);
      const errorMsg = error?.message || "Failed to remove member. Please try again.";
      showTransientNotification(errorMsg, "#ff6b6b");
      throw error;
    }
  };

  const handleLeaveGroupConfirm = async ({ transferToUserId } = {}) => {
    if (!conversation || conversation.type !== "group" || !currentUser?.token) return;
    if (isLeavingGroup) return;

    try {
      setIsLeavingGroup(true);
      const response = await messengerService.leaveGroup(
        conversation._id,
        currentUser.token,
        transferToUserId ? { transferToUserId } : undefined
      );

      const groupName = conversation.name || "the group";
      const notification = document.createElement("div");
      notification.textContent = transferToUserId
        ? `Admin transferred — you left ${groupName}`
        : `You left ${groupName}`;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #51cf66;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (notification.parentNode) notification.parentNode.removeChild(notification);
      }, 3000);

      setShowLeaveGroupModal(false);
      setShowGroupInfoModal(false);

      if (response?.isAdminTransferLeave) {
        // Explicit admin transfer is a clean handoff: the previous admin loses
        // all access to the chat. Remove the conversation from this user's UI
        // entirely instead of flipping it to read-only.
        if (onDeleteGroup) {
          onDeleteGroup(conversation._id);
        } else if (onBack) {
          onBack();
        }
      } else if (onLeaveGroup) {
        // Regular leave — keep the chat in the conversations list as
        // read-only so the user can still browse history.
        onLeaveGroup(conversation._id, {
          leftAt: response?.leftAt || new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Error leaving group:", error);
      const errorMsg = error?.message || "Failed to leave group. Please try again.";
      const notification = document.createElement("div");
      notification.textContent = errorMsg;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ff6b6b;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (notification.parentNode) notification.parentNode.removeChild(notification);
      }, 3000);
    } finally {
      setIsLeavingGroup(false);
    }
  };

  // Disband the entire group. Only available to the owner when they're the
  // only remaining member — handled by the LeaveGroupModal once it detects
  // there's no one to transfer ownership to.
  const handleDisbandGroupConfirm = async () => {
    if (!conversation || conversation.type !== "group" || !currentUser?.token) return;
    if (isLeavingGroup) return;

    try {
      setIsLeavingGroup(true);
      await messengerService.disbandGroup(conversation._id, currentUser.token);

      const groupName = conversation.name || "the group";
      showTransientNotification(`${groupName} disbanded`);

      setShowLeaveGroupModal(false);
      setShowGroupInfoModal(false);

      // Treat disband like a permanent conversation removal so the chat
      // drops out of the user's list and the chat window closes.
      if (onDeleteGroup) {
        onDeleteGroup(conversation._id);
      } else if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Error disbanding group:", error);
      const errorMsg = error?.message || "Failed to disband group. Please try again.";
      showTransientNotification(errorMsg, "#ff6b6b");
      throw error;
    } finally {
      setIsLeavingGroup(false);
    }
  };

  // Delete the group conversation from this user's view (only available
  // after they have left the group). Calls the regular deleteConversation
  // endpoint, which on the backend just removes them from leftMembers so
  // the chat disappears from their list while staying intact for others.
  const handleDeleteGroupConfirm = async () => {
    if (!conversation || conversation.type !== "group" || !currentUser?.token) return;
    if (isDeletingGroup) return;

    try {
      setIsDeletingGroup(true);
      await messengerService.deleteConversation(conversation._id, currentUser.token);

      const groupName = conversation.name || "the group";
      const notification = document.createElement("div");
      notification.textContent = `${groupName} deleted from your chats`;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #51cf66;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (notification.parentNode) notification.parentNode.removeChild(notification);
      }, 3000);

      setShowDeleteGroupModal(false);
      setShowGroupInfoModal(false);

      if (onDeleteGroup) {
        onDeleteGroup(conversation._id);
      } else if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Error deleting group:", error);
      const errorMsg = error?.message || "Failed to delete group. Please try again.";
      const notification = document.createElement("div");
      notification.textContent = errorMsg;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ff6b6b;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (notification.parentNode) notification.parentNode.removeChild(notification);
      }, 3000);
    } finally {
      setIsDeletingGroup(false);
    }
  };

  const handleDeleteConversation = async (deleteForEveryone = false) => {
    try {
      // You would typically make an API call here to delete the conversation
      // await messengerService.deleteConversation(conversation._id, deleteForEveryone, currentUser.token);
      
      const action = deleteForEveryone ? 'deleted for everyone' : 'deleted for you';
      const notification = document.createElement('div');
      notification.textContent = `Conversation ${action} successfully`;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #51cf66;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      
      document.body.appendChild(notification);
      
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 3000);
      
      // Go back to conversations list
      onBack();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  const getStarredMessageIds = () => {
    try {
      const store = JSON.parse(localStorage.getItem('starredMessages') || '{}');
      return (messages || [])
        .filter((message) => {
          if (!message || !message._id) return false;
          if (store && store[message._id]) return true;
          return !!(message.starred || message.isStarred);
        })
        .map((message) => message._id);
    } catch (error) {
      console.error('Error reading starred messages from storage:', error);
      return (messages || [])
        .filter((message) => message && message._id && (message.starred || message.isStarred))
        .map((message) => message._id);
    }
  };

  const handleClearConversation = async () => {
    try {
      const starredMessageIds = getStarredMessageIds();

      // Call the API to clear the conversation on the server, preserving starred messages
      await messengerService.clearConversation(
        conversation._id,
        currentUser.token,
        { excludeMessageIds: starredMessageIds }
      );
      
      // Close the modal first
      setShowClearModal(false);
      
      // Clear messages from the current view with animation
      const messageElements = document.querySelectorAll('.message_bubble');
      messageElements.forEach(element => {
        const messageId = element.getAttribute('data-message-id');
        if (starredMessageIds.includes(messageId)) {
          // Highlight preserved starred messages
          element.classList.add('starred_preserved');
          setTimeout(() => element.classList.remove('starred_preserved'), 1500);
          return;
        }
        element.style.animation = 'fadeOut 0.3s ease-out';
        setTimeout(() => {
          element.remove();
        }, 300);
      });
      
      // Call the parent component to clear messages from state
      if (onClearMessages) {
        onClearMessages(conversation._id, starredMessageIds);
      }
      
      // Show success notification
      const notification = document.createElement('div');
      notification.textContent = 'Conversation cleared successfully';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #51cf66;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      
      document.body.appendChild(notification);
      
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 3000);
      
    } catch (error) {
      console.error('Error clearing conversation:', error);
      
      // Close modal even on error
      setShowClearModal(false);
      
      // Show error notification
      const errorNotification = document.createElement('div');
      errorNotification.textContent = 'Failed to clear conversation. Please try again.';
      errorNotification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ff6b6b;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      `;
      
      document.body.appendChild(errorNotification);
      
      setTimeout(() => {
        if (errorNotification.parentNode) {
          errorNotification.parentNode.removeChild(errorNotification);
        }
      }, 3000);
    }
  };

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleStarredUpdated = () => setStarRefreshKey((k) => k + 1);
    window.addEventListener('starredMessagesUpdated', handleStarredUpdated);
    return () => window.removeEventListener('starredMessagesUpdated', handleStarredUpdated);
  }, []);

  useEffect(() => {
    if (!showGroupInfoModal || !currentUser?.token) return;

    let isMounted = true;

    const fetchRelationshipData = async () => {
      try {
        const [friendsInfoResponse, customListsResponse] = await Promise.all([
          getFriendsPageInfos(currentUser.token),
          getCustomLists(currentUser.token),
        ]);
        if (!isMounted) return;
        if (friendsInfoResponse?.status === "ok") {
          const fetchedFollowing = Array.isArray(friendsInfoResponse?.data?.following)
            ? friendsInfoResponse.data.following
            : [];
          setFollowingIds(fetchedFollowing);
        }

        if (customListsResponse?.status === "ok") {
          const allLists = Array.isArray(customListsResponse?.data?.allLists)
            ? customListsResponse.data.allLists
            : [
                ...(customListsResponse?.data?.ownedLists || []),
                ...(customListsResponse?.data?.memberLists || []),
              ];
          const matchedList = resolveGroupListForConversation(allLists);
          setCurrentGroupList(matchedList);
          if (matchedList?.groupPicture) {
            setGroupPictureOverride(matchedList.groupPicture);
          }
        }
      } catch (error) {
        console.error("Failed to fetch following relationship data:", error);
      }
    };

    fetchRelationshipData();

    return () => {
      isMounted = false;
    };
  }, [showGroupInfoModal, currentUser?.token, conversation?._id, resolveGroupListForConversation]);

  return (
    <div className="chat_window">
      <ChatHeader
        conversation={conversation}
        groupPictureOverride={groupPictureOverride}
        messages={messages}
        currentUser={currentUser}
        onStartVoiceCall={() => onStartCall('voice')}
        onStartVideoCall={() => onStartCall('video')}
        onBack={onBack}
        onSearchInConversation={() => {
          setIsSearching(true);
        }}
        onToggleMute={() => {
          // Show mute duration modal instead of directly toggling
          setShowMuteDurationModal(true);
        }}
        onShowCallHistory={onShowCallHistory}
        onThemeSettings={() => setShowThemeManager(true)}
        onDeleteConversation={() => {
          setShowDeleteModal(true);
        }}
        onClearConversation={() => {
          setShowClearModal(true);
        }}
        onConversationPinToggle={onConversationPinToggle}
        isBlocked={iBlockedOtherUser}
        onBlockUser={async (userId) => {
          try {
            await messengerService.blockUser(userId, currentUser.token);
            console.log('User blocked successfully:', userId);
            window.dispatchEvent(new CustomEvent('blockedUsersUpdated'));
          } catch (error) {
            console.error('Error blocking user:', error);
          }
        }}
        onUnblockUser={async (userId) => {
          try {
            await messengerService.unblockUser(userId, currentUser.token);
            console.log('User unblocked successfully:', userId);
            window.dispatchEvent(new CustomEvent('blockedUsersUpdated'));
          } catch (error) {
            console.error('Error unblocking user:', error);
          }
        }}
        onReportUser={async (userId, reason) => {
          try {
            await messengerService.reportUser(userId, reason, currentUser.token);
            console.log('User reported successfully:', userId);
          } catch (error) {
            console.error('Error reporting user:', error);
          }
        }}
        onViewProfile={(userId) => {
          navigateToProfileByUserId(userId);
        }}
        onOpenGroupInfo={() => {
          if (conversation.type === 'group') {
            setShowGroupInfoModal(true);
          }
        }}
        onShareContact={async (userId) => {
          try {
            await messengerService.shareContact(userId, currentUser.token);
            console.log('Contact shared successfully:', userId);
          } catch (error) {
            console.error('Error sharing contact:', error);
          }
        }}
        onExportChat={async (conversationId) => {
          try {
            await messengerService.exportChat(conversationId, currentUser.token);
            console.log('Chat exported successfully:', conversationId);
          } catch (error) {
            console.error('Error exporting chat:', error);
          }
        }}
        onArchiveConversation={async (conversationId) => {
          try {
            await messengerService.archiveConversation(conversationId, currentUser.token);
            console.log('Conversation archived successfully:', conversationId);
          } catch (error) {
            console.error('Error archiving conversation:', error);
          }
        }}
        // Selection mode controls
        isSelecting={isSelecting}
        selectedCount={selectedMessageIds.size}
        onStartSelectMessages={startSelectingMessages}
        onCancelSelectMessages={cancelSelectingMessages}
        onStarSelected={starSelectedMessages}
        onUnstarSelected={unstarSelectedMessages}
        onCopySelected={copySelectedMessages}
        onDeleteSelected={requestDeleteSelectedMessages}
        allSelectedAreStarred={allSelectedAreStarred}
        canDeleteForEveryone={canDeleteSelectedForEveryone}
        canShowSelectedMessageInfo={canShowSelectedMessageInfo}
        onShowSelectedMessageInfo={() => {
          if (selectedSingleMessage) setMessageInfoMessage(selectedSingleMessage);
        }}
        onlineUsers={onlineUsers}
      />

      <div className="messages_container">
        {/* Search Interface */}
        {isSearching && (
          <div className="search_interface">
            <div className="search_input_container">
              <input
                type="text"
                placeholder="Search in conversation..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="search_input"
                autoFocus
              />
              <button 
                className="search_close_btn"
                onClick={closeSearch}
                title="Close search"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            
            {searchQuery && (
              <div className="search_results_info">
                <span className="search_count">
                  {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                </span>
                {searchResults.length > 0 && (
                  <div className="search_navigation">
                    <button 
                      className="search_nav_btn"
                      onClick={goToPreviousResult}
                      title="Previous result"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                      </svg>
                    </button>
                    <span className="search_position">
                      {currentSearchIndex + 1} of {searchResults.length}
                    </span>
                    <button 
                      className="search_nav_btn"
                      onClick={goToNextResult}
                      title="Next result"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <MessagesList
          messages={messages}
          currentUser={currentUser}
          onReact={handleReactToMessage}
          onDelete={handleDeleteMessage}
          onReply={handleReplyToMessage}
          onEdit={handleEditMessage}
          onForward={handleForwardMessage}
          conversation={conversation}
          disableInteractions={isReadOnlyConversation}
          // Selection props
          isSelecting={isSelecting}
          selectedMessageIds={selectedMessageIds}
          onToggleSelect={toggleSelectMessage}
          starRefreshKey={starRefreshKey}
          onShowMessageInfo={(msg) => setMessageInfoMessage(msg)}
        />
        
        <TypingIndicator
          typingUsers={typingUsers}
          conversation={conversation}
          currentUser={currentUser}
        />
        
        <div ref={messagesEndRef} />
      </div>

      <div className="message_input_container">
        {replyingTo && (
          <div className="reply_preview">
            <div className="reply_content">
              <span className="reply_to">
                Replying to {replyingTo.sender.first_name}
              </span>
              <p className="reply_message">
                {replyingTo.type === 'text' 
                  ? replyingTo.content.text 
                  : `${replyingTo.type} message`
                }
              </p>
            </div>
            <button className="cancel_reply_btn" onClick={handleCancelReply}>×</button>
          </div>
        )}

        {hasUserLeftGroup ? (
          <div className="readonly_chat_banner" role="status">
            <span className="readonly_chat_banner__title">
              You left this group
            </span>
            <span className="readonly_chat_banner__subtitle">
              You can no longer send or receive messages here, but the
              conversation history is still available.
            </span>
          </div>
        ) : iBlockedOtherUser ? (
          <div
            className="readonly_chat_banner blocked_chat_banner"
            role="status"
          >
            <span className="readonly_chat_banner__title">
              You blocked this user
            </span>
            <span className="readonly_chat_banner__subtitle">
              You can&apos;t send or receive messages while this user is
              blocked. Unblock them to continue the conversation.
            </span>
            <button
              type="button"
              className="blocked_chat_banner__unblock"
              onClick={handleUnblockFromBanner}
            >
              Unblock
            </button>
          </div>
        ) : (
          <MessageInput
            onSendMessage={handleSendMessage}
            onTypingStart={handleTypingStart}
            onTypingStop={handleTypingStop}
            onFileSelect={handleFileSelect}
            showEmojiPicker={showEmojiPicker}
            onToggleEmojiPicker={() => setShowEmojiPicker(!showEmojiPicker)}
            conversationId={conversation._id}
            onSendMedia={onSendMedia}
            currentUser={currentUser}
          />
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Mute Duration Modal */}
      <MuteDurationModal
        conversation={conversation}
        currentUser={currentUser}
        onMute={handleMute}
        onClose={() => setShowMuteDurationModal(false)}
        isVisible={showMuteDurationModal}
      />

      {/* Delete Conversation Modal */}
      <DeleteConversationModal
        conversation={conversation}
        currentUser={currentUser}
        onDelete={handleDeleteConversation}
        onClose={() => setShowDeleteModal(false)}
        isVisible={showDeleteModal}
      />

      {/* Clear Conversation Modal */}
      <ClearConversationModal
        conversation={conversation}
        currentUser={currentUser}
        onClear={handleClearConversation}
        onClose={() => setShowClearModal(false)}
        isVisible={showClearModal}
      />

      {/* Theme Manager Modal */}
      <ThemeManager
        isOpen={showThemeManager}
        onClose={() => setShowThemeManager(false)}
        currentTheme={currentTheme}
        onThemeChange={handleThemeChange}
        conversationId={conversation?._id}
      />

      <GroupInfoModal
        conversation={conversation}
        currentUser={currentUser}
        followingIds={followingIds}
        groupMembers={currentGroupList?.members || []}
        groupOwnerId={currentGroupList?.owner?._id || currentGroupList?.owner?.id || currentGroupList?.owner}
        groupPicture={groupPictureOverride}
        isOpen={showGroupInfoModal}
        onClose={() => setShowGroupInfoModal(false)}
        onOpenMemberProfile={navigateToProfileByUserId}
        canEditGroupPhoto={canEditGroupPhoto}
        onChangeGroupPhoto={handleGroupPhotoChange}
        isUploadingGroupPhoto={isUploadingGroupPhoto}
        isCurrentUserAdmin={isCurrentUserGroupAdmin}
        onRemoveMember={handleRemoveGroupMember}
        onLeaveGroup={
          conversation?.type === "group" && !hasUserLeftGroup
            ? () => setShowLeaveGroupModal(true)
            : null
        }
        onDeleteGroup={
          conversation?.type === "group" && hasUserLeftGroup
            ? () => setShowDeleteGroupModal(true)
            : null
        }
        userLeft={hasUserLeftGroup}
      />

      <LeaveGroupModal
        conversation={conversation}
        currentUser={currentUser}
        members={currentGroupList?.members || []}
        isCurrentUserOwner={isCurrentUserGroupOwner}
        isVisible={showLeaveGroupModal}
        onClose={() => {
          if (!isLeavingGroup) setShowLeaveGroupModal(false);
        }}
        onConfirm={handleLeaveGroupConfirm}
        onDisband={handleDisbandGroupConfirm}
      />

      <DeleteGroupModal
        conversation={conversation}
        isVisible={showDeleteGroupModal}
        onClose={() => {
          if (!isDeletingGroup) setShowDeleteGroupModal(false);
        }}
        onConfirm={handleDeleteGroupConfirm}
      />

      {messageInfoMessage && (
        <MessageInfoModal
          message={messageInfoMessage}
          conversation={conversation}
          onClose={() => setMessageInfoMessage(null)}
        />
      )}

      {showDeleteSelectedModal && (
        <div
          className="leave_group_modal_overlay"
          onClick={() => {
            if (!isDeletingSelected) setShowDeleteSelectedModal(false);
          }}
        >
          <div
            className="leave_group_modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="leave_group_header">
              <h3>
                Delete {selectedMessageIds.size} message
                {selectedMessageIds.size === 1 ? '' : 's'}?
              </h3>
            </div>
            <div className="leave_group_content">
              <div className="leave_group_icon">🗑</div>
              <p className="leave_group_description">
                {canDeleteSelectedForEveryone
                  ? 'Choose how you want to delete these messages.'
                  : 'These messages will be deleted from your chat.'}
              </p>
              <p className="leave_group_note">
                This action cannot be undone.
              </p>
            </div>
            <div className="leave_group_actions" style={{ flexWrap: 'wrap' }}>
              <button
                type="button"
                className="cancel_btn"
                disabled={isDeletingSelected}
                onClick={() => setShowDeleteSelectedModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="leave_btn"
                disabled={isDeletingSelected}
                onClick={() => confirmDeleteSelectedMessages(false)}
              >
                {isDeletingSelected ? 'Deleting…' : 'Delete for me'}
              </button>
              {canDeleteSelectedForEveryone && (
                <button
                  type="button"
                  className="leave_btn delete_btn"
                  disabled={isDeletingSelected}
                  onClick={() => confirmDeleteSelectedMessages(true)}
                >
                  {isDeletingSelected ? 'Deleting…' : 'Delete for everyone'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <ForwardMessageModal
        isOpen={showForwardModal}
        conversations={conversations}
        selectedConversationId={conversation?._id}
        currentUser={currentUser}
        isSubmitting={isForwarding}
        onClose={() => {
          if (isForwarding) return;
          setShowForwardModal(false);
          setForwardTargetMessage(null);
        }}
        onConfirm={handleConfirmForward}
      />
    </div>
  );
} 
